import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  getDocs
} from 'firebase/firestore';
import '../PaymentRequests.css';

function PaymentRequests() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [agentsMap, setAgentsMap] = useState({}); // Stores agentPhone -> agentName mapping
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReq, setSelectedReq] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const rowsPerPage = 50;

  // 1. Fetch Agents Mapping (Phone -> Name)
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const agentsSnap = await getDocs(collection(db, 'agents'));
        const map = {};
        agentsSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.phone) {
            map[data.phone] = data.agentName || 'Unknown Agent';
          }
          map[docSnap.id] = data.agentName || 'Unknown Agent';
        });
        setAgentsMap(map);
      } catch (err) {
        console.error("Error fetching agents map:", err);
      }
    };

    fetchAgents();
  }, []);

  // 2. Realtime Firestore Listener for Payment Requests
  useEffect(() => {
    const q = query(
      collection(db, 'paymentRequests'),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setRequests(data);
      setFilteredRequests(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 3. Filter & Search Logic
  useEffect(() => {
    let result = requests;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((req) => {
        const phone = (req.agentPhone || '').toString().toLowerCase();
        const agentName = (agentsMap[req.agentPhone] || '').toLowerCase();
        return phone.includes(term) || agentName.includes(term);
      });
    }

    if (statusFilter) {
      result = result.filter(
        (req) => (req.status || '').toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (dateFilter) {
      result = result.filter((req) => {
        const reqDate = req.requestedAt?.toDate()?.toISOString()?.split('T')[0];
        return reqDate === dateFilter;
      });
    }

    setFilteredRequests(result);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, requests, agentsMap]);

  // 4. Update Status Function
  const handleStatusChange = async (requestId, newStatus) => {
    try {
      setUpdatingId(requestId);
      const reqRef = doc(db, 'paymentRequests', requestId);
      await updateDoc(reqRef, {
        status: newStatus
      });
      setUpdatingId(null);
    } catch (error) {
      console.error("Error updating status: ", error);
      alert("Failed to update status. Please try again.");
      setUpdatingId(null);
    }
  };

  // Pagination Math
  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedRequests = filteredRequests.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const getStatusClass = (statusStr) => {
    const st = (statusStr || '').toLowerCase();
    if (st.includes('paid')) return 'status-paid';
    if (st.includes('processing')) return 'status-processing';
    if (st.includes('rejected')) return 'status-rejected';
    return 'status-pending';
  };

  if (loading) {
    return <div className="admin-loader">Loading Payment Requests...</div>;
  }

  return (
    <div className="orders-container">
      {/* Search & Filter Bar */}
      <div className="filter-bar">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by Agent Name or Phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-options">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="Amount pending">Amount Pending</option>
            <option value="Processing">Processing</option>
            <option value="Paid">Paid</option>
            <option value="Request rejected">Request Rejected</option>
          </select>

          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="filter-date-input"
          />

          {(searchTerm || dateFilter || statusFilter) && (
            <button
              className="clear-btn"
              onClick={() => {
                setSearchTerm('');
                setDateFilter('');
                setStatusFilter('');
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Main Table */}
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Agent Name</th>
              <th>Requested Date</th>
              <th>Agent Earnings</th>
              <th>Orders</th>
              <th>Status</th>
              <th>Update Status</th>
            </tr>
          </thead>

          <tbody>
            {paginatedRequests.length > 0 ? (
              paginatedRequests.map((req, index) => (
                <tr
                  key={req.id}
                  onClick={() => setSelectedReq(req)}
                  style={{ cursor: 'pointer' }}
                  className="table-row-hover"
                >
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <strong className="agent-name-text">
                      {agentsMap[req.agentPhone] || 'N/A'}
                    </strong>
                  </td>
                  <td>
                    {req.requestedAt?.toDate()?.toLocaleString() || '-'}
                  </td>
                  <td className="amount-earnings">
                    AED {req.myEarnings ?? 0}
                  </td>
                  <td>
                    <span className="orders-badge">{req.totalOrdersCount ?? 0}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(req.status)}`}>
                      {req.status || 'Pending'}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className="status-dropdown"
                      value={req.status || 'Amount pending'}
                      disabled={updatingId === req.id}
                      onChange={(e) => handleStatusChange(req.id, e.target.value)}
                    >
                      <option value="Amount pending">Amount Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Paid">Paid</option>
                      <option value="Request rejected">Request Rejected</option>
                    </select>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>
                  No payment requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <button
            className="clear-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Previous
          </button>
          <span className="page-indicator">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="clear-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Detail Modal Popup */}
      {selectedReq && (
        <div className="modal-overlay" onClick={() => setSelectedReq(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Payment Request Breakdown</h2>
                <span className={`status-badge ${getStatusClass(selectedReq.status)}`}>
                  {selectedReq.status || 'Pending'}
                </span>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedReq(null)}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3><i className="fas fa-user-circle"></i> Agent Information</h3>
                <div className="details-grid">
                  <div><strong>Agent Name:</strong> {agentsMap[selectedReq.agentPhone] || 'N/A'}</div>
                  <div><strong>Agent Phone:</strong> {selectedReq.agentPhone || '-'}</div>
                  <div><strong>Filter Type:</strong> {selectedReq.filterType || '-'}</div>
                  
                  <div className="full-width">
                    <strong>Requested Date:</strong> {selectedReq.requestedAt?.toDate()?.toLocaleString() || '-'}
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3><i className="fas fa-wallet"></i> Settlement Details</h3>
                <div className="details-grid">
                  <div><strong>Total Amount:</strong> AED {selectedReq.totalAmount ?? 0}</div>
                  <div><strong>Agent Earnings:</strong> AED {selectedReq.myEarnings ?? 0}</div>
                  <div><strong>Mr White Share:</strong> AED {selectedReq.mrWhiteAmount ?? 0}</div>
                  <div><strong>Total Orders:</strong> {selectedReq.totalOrdersCount ?? 0}</div>
                </div>
              </div>

              <div className="modal-section">
                <h3><i className="fas fa-calendar-alt"></i> Settlement Duration</h3>
                <div className="details-grid">
                  <div><strong>Start Date:</strong> {selectedReq.startDate?.toDate()?.toLocaleString() || '-'}</div>
                  <div><strong>End Date:</strong> {selectedReq.endDate?.toDate()?.toLocaleString() || '-'}</div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="clear-btn" onClick={() => setSelectedReq(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentRequests;